import React from "react";
import { useTranslation } from "react-i18next";
import LegalPageShell from "../../components/LegalPageShell";

export default function CommunityGuidelines() {
  const { t } = useTranslation(["legalCommunity", "common", "legal"]);

  const sections = [
    { id: "introduction", label: t("introduction", "Introduction") },
    { id: "community-standard", label: t("communityStandard", "Community Standard") },
    { id: "listings-integrity", label: t("listingsAndExchangeIntegrity", "Listings and Exchange Integrity") },
    { id: "messaging-rules", label: t("messagingAndInteractionRules", "Messaging and Interaction Rules") },
    { id: "reporting-moderation", label: t("reportingAndModeration", "Reporting and Moderation") },
  ];

  return (
<LegalPageShell
        title={t("title", "Community Guidelines")}
        subtitle={t("subtitle", "Standards for participation in the Orrith community.")}
        effectiveDate={`Effective: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
        sections={sections}
        namespace="legalCommunity"
      >
      <section id="introduction" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("introduction", "Introduction")}
        </h2>
        <p className="legal-prose">
          {t("communityIntro", "Orrith is built for practical trust. Every user interaction should support fairness, safety, and dignity. These guidelines apply to all platform spaces, including product listings, direct messages, support tickets, ratings, and public profile activity. By participating in the Orrith community, you agree to follow these guidelines.")}
        </p>
        <p className="legal-prose mt-4">
          {t("communityPurpose", "These guidelines exist to create an environment where farmers, buyers, and community members can coordinate with confidence. They are not exhaustive—we reserve the right to take action against behavior that undermines trust or safety even if not explicitly listed here.")}
        </p>
      </section>

      <section id="community-standard" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("communityStandard", "Community Standard")}
        </h2>
        <p className="legal-prose mb-6">
          {t("communityStandardDesc", "Three principles guide all interactions on Orrith:")}
        </p>

        <div className="grid gap-4 sm:grid-cols-3 mt-4 mb-8">
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1.5">
              {t("truthfulness", "Truthfulness")}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("truthfulnessDesc", "Share accurate details in listings, profiles, and messages.")}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1.5">
              {t("respect", "Respect")}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("respectDesc", "Communicate professionally and avoid harassment, threats, and discriminatory language.")}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 bg-gray-50 dark:bg-neutral-900/40">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1.5">
              {t("responsibility", "Responsibility")}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("responsibilityDesc", "Follow through on commitments for pickup windows, offers, and confirmations.")}
            </p>
          </div>
        </div>
      </section>

      <section id="listings-integrity" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("listingsAndExchangeIntegrity", "Listings and Exchange Integrity")}
        </h2>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("requiredBehavior", "Required Behavior")}
        </h3>
        <ul className="legal-list">
          <li>{t("requiredBehaviorItems.accurateDetails", "Use accurate names, photos, quantity, and location details")}</li>
          <li>{t("requiredBehaviorItems.currentStatus", "Keep status current when items are no longer available")}</li>
          <li>{t("requiredBehaviorItems.noMisleading", "Do not post intentionally misleading or fraudulent content")}</li>
          <li>{t("requiredBehaviorItems.noImpersonation", "Do not use copied media to impersonate ownership or availability")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("notAllowed", "Not Allowed")}
        </h3>
        <ul className="legal-list">
          <li>{t("notAllowedItems.scamListings", "Scam, bait, or impersonation listings")}</li>
          <li>{t("notAllowedItems.manipulatedClaims", "Manipulated claim or offer behavior intended to deceive")}</li>
          <li>{t("notAllowedItems.reposting", "Repeated reposting patterns intended to spam visibility")}</li>
          <li>{t("notAllowedItems.abusiveLocation", "Abusive location-sharing behavior against user consent choices")}</li>
        </ul>
      </section>

      <section id="messaging-rules" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("messagingAndInteractionRules", "Messaging and Interaction Rules")}
        </h2>
        <p className="legal-prose mb-6">
          {t("messagingIntro", "Direct communication between users should be purposeful and respectful:")}
        </p>
        <ul className="legal-list">
          <li>{t("messaging.relevant", "Keep communication relevant to the transaction or support context")}</li>
          <li>{t("messaging.noAbusive", "Do not send abusive, threatening, or sexually explicit messages")}</li>
          <li>{t("messaging.noSpam", "Do not repeatedly spam users or manipulate chat threads")}</li>
          <li>{t("messaging.respectPrivacy", "Respect privacy boundaries and do not pressure users for private data")}</li>
        </ul>
      </section>

      <section id="reporting-moderation" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("reportingAndModeration", "Reporting and Moderation")}
        </h2>
        <p className="legal-prose mb-6">
          {t("reporting.desc", "Orrith provides support and moderation mechanisms for trust and safety. Depending on context, content or account behavior may be flagged, reviewed, hidden, removed, or restricted.")}
        </p>
        <ul className="legal-list">
          <li>{t("reporting.useChannels", "Use available report/support channels when policy violations occur")}</li>
          <li>{t("reporting.provideEvidence", "Provide clear reasons and evidence when submitting reports")}</li>
          <li>{t("reporting.adminOutcomes", "Administrative review outcomes may include warnings, restrictions, or bans")}</li>
          <li>{t("reporting.severeViolations", "Repeated severe violations may result in permanent account removal")}</li>
        </ul>

        <p className="legal-prose mt-6">
          {t("reportingAppeals", "If you believe an enforcement action was taken in error, you may appeal through the Support Center. Include the enforcement reference, your perspective, and any supporting context. Appeals are reviewed by a different moderator than the one who took the original action.")}
        </p>
      </section>
    </LegalPageShell>
  );
}